'use client';

import { c as _c } from "react/compiler-runtime";
import type { HeroState } from './utils';
import type React from 'react';
import ArrowUpwardRounded from '@mui/icons-material/ArrowUpwardRounded';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { motion } from 'motion/react';
import { useState } from 'react';
import { FlexColumn, FlexRow } from 'src/components/Atoms/FlexBox';
import { TextInput } from 'src/components/Molecules/TextInput';
import { openAskMeAnything } from 'src/components/Organisms/AskMeAnything/lib/open-event';
import { HERO_MOTION } from './motion';
import StatusDot from './StatusDot';
interface HeroAskMeAnythingProps {
  heroState: HeroState;
  teamName: string;
  onConnect: () => void;
}
const EXAMPLE_QUESTIONS = ['Which channel brought the most leads last month?', 'Where can budget move to create more impact?', 'What changed in website traffic this week?'];
const PLACEHOLDERS: Record<HeroState, string> = {
  loading: 'Loading your data…',
  'no-connections': 'Connect a platform to ask questions about your data',
  'no-data': 'Ask me anything once your data lands…',
  normal: 'Ask anything about your data...'
};
const getSubtitle = (heroState: HeroState, teamName: string) => {
  if (heroState === 'no-connections') {
    return `Your AI analyst wakes up as soon as ${teamName} has its first connection`;
  }
  if (heroState === 'no-data') {
    return `Your AI analyst for ${teamName} — standing by for the first data`;
  }
  return `Your AI analyst for ${teamName}`;
};
const Section = styled(motion.section)(({
  theme
}) => ({
  borderRadius: theme.sizes.radius['3xl'],
  padding: `${theme.sizes.spacing['10']} ${theme.sizes.spacing['08']}`,
  [theme.breakpoints.up('lg')]: {
    padding: `${theme.sizes.spacing['13']} ${theme.sizes.spacing['10']}`
  }
}));
const Content = styled(FlexColumn)(({
  theme
}) => ({
  width: '100%',
  maxWidth: '42rem',
  marginInline: 'auto',
  alignItems: 'center',
  textAlign: 'center',
  gap: theme.sizes.spacing['08']
}));

// A one-off prompt field shaped like the prototype's pill, using theme tokens only.
const PromptForm = styled('form')(({
  theme
}) => ({
  width: '100%',
  '& .MuiFormControl-root': {
    width: '100%'
  },
  '& .MuiFormControl-root .MuiInputBase-root': {
    height: theme.sizes.spacing['12'],
    borderRadius: theme.sizes.radius.full,
    paddingLeft: theme.sizes.spacing['08'],
    paddingRight: theme.sizes.spacing['03'],
    border: `${theme.sizes.borderWidth[1]} solid ${theme.colors.border.secondary}`,
    backgroundColor: theme.colors.surface.primary,
    boxShadow: theme.boxShadows[1],
    transition: 'box-shadow 0.2s',
    '&:hover': {
      boxShadow: theme.boxShadows[2]
    },
    '&.Mui-focused': {
      borderColor: theme.colors.brand.blaze[500]
    }
  },
  '& .MuiFormControl-root .MuiInputBase-input': {
    background: 'transparent',
    fontSize: theme.fonts.size.base
  }
}));
const SubmitButton = styled(IconButton)(({
  theme
}) => ({
  flexShrink: 0,
  width: theme.sizes.spacing['10'],
  height: theme.sizes.spacing['10'],
  backgroundColor: theme.colors.brand.blaze[500],
  color: theme.colors.text.inverted,
  '&:hover': {
    backgroundColor: theme.colors.brand.blaze[600]
  },
  '&.Mui-disabled': {
    backgroundColor: theme.colors.surface.tertiary,
    color: theme.colors.icon.disabled
  }
}));
const HeroAskMeAnything: React.FC<HeroAskMeAnythingProps> = t0 => {
  const $ = _c(41);
  const {
    heroState,
    teamName,
    onConnect
  } = t0;
  const [question, setQuestion] = useState("");
  const {
    y,
    duration,
    delay
  } = HERO_MOTION.askMeAnything;
  const canAsk = heroState === "normal" || heroState === "no-data";
  const isWaitingForConnections = heroState === "no-connections";
  let t1;
  if ($[0] !== canAsk || $[1] !== question) {
    t1 = event => {
      event.preventDefault();
      if (!canAsk) {
        return;
      }
      const text = question.trim();
      openAskMeAnything(text ? {
        question: text
      } : {});
      setQuestion("");
    };
    $[0] = canAsk;
    $[1] = question;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleSubmit = t1;
  let t2;
  let t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      opacity: 0,
      y
    };
    t3 = {
      opacity: 1,
      y: 0
    };
    t4 = {
      duration,
      delay
    };
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t2 = $[3];
    t3 = $[4];
    t4 = $[5];
  }
  let t5;
  if ($[6] !== isWaitingForConnections) {
    t5 = !isWaitingForConnections && <Chip size="small" variant="outlined" label="AI analyst" />;
    $[6] = isWaitingForConnections;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Typography variant="heading-3xl" component="h2" sx={_temp2}>Ask me anything</Typography>;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== heroState || $[10] !== teamName) {
    t7 = getSubtitle(heroState, teamName);
    $[9] = heroState;
    $[10] = teamName;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t7) {
    t8 = <Typography variant="body-sm" sx={_temp3}>{t7}</Typography>;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t5 || $[15] !== t8) {
    t9 = <FlexColumn sx={_temp}>{t5}{t6}{t8}</FlexColumn>;
    $[14] = t5;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      "aria-label": "Ask me anything"
    };
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = event_0 => setQuestion(event_0.target.value);
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const t12 = PLACEHOLDERS[heroState];
  const t13 = !canAsk;
  let t14;
  if ($[19] !== heroState || $[20] !== isWaitingForConnections) {
    t14 = heroState === "loading" ? <StatusDot tone="waiting" pulsing={true} /> : <AutoAwesomeIcon fontSize="small" sx={theme_2 => ({
      color: isWaitingForConnections ? theme_2.colors.text.brand : theme_2.colors.icon.secondary
    })} />;
    $[19] = heroState;
    $[20] = isWaitingForConnections;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  const t15 = !canAsk;
  let t16;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <ArrowUpwardRounded fontSize="small" />;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  let t17;
  if ($[23] !== t15) {
    t17 = <SubmitButton type="submit" aria-label="Ask" disabled={t15}>{t16}</SubmitButton>;
    $[23] = t15;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  let t18;
  if ($[25] !== question || $[26] !== t12 || $[27] !== t13 || $[28] !== t14 || $[29] !== t17) {
    t18 = <TextInput showLabel={false} name="hero-ask-me-anything" inputProps={t10} value={question} onChange={t11} placeholder={t12} disabled={t13} autoComplete="off" startAdornment={t14} endAdornment={t17} />;
    $[25] = question;
    $[26] = t12;
    $[27] = t13;
    $[28] = t14;
    $[29] = t17;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] !== handleSubmit || $[32] !== t18) {
    t19 = <PromptForm onSubmit={handleSubmit}>{t18}</PromptForm>;
    $[31] = handleSubmit;
    $[32] = t18;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  let t20;
  if ($[34] !== isWaitingForConnections || $[35] !== onConnect) {
    t20 = isWaitingForConnections && <FlexColumn sx={_temp4}><Button variant="contained" onClick={onConnect}>Connect a platform</Button><FlexColumn sx={_temp5}><Typography variant="body-xs" sx={_temp6}>Questions you'll be able to ask</Typography><FlexRow sx={_temp7}>{EXAMPLE_QUESTIONS.map(_temp8)}</FlexRow></FlexColumn></FlexColumn>;
    $[34] = isWaitingForConnections;
    $[35] = onConnect;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] !== t19 || $[38] !== t20 || $[39] !== t9) {
    t21 = <Section initial={t2} animate={t3} transition={t4}><Content>{t9}{t19}{t20}</Content></Section>;
    $[37] = t19;
    $[38] = t20;
    $[39] = t9;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  return t21;
};
export default HeroAskMeAnything;
function _temp(theme) {
  return {
    alignItems: "center",
    gap: theme.sizes.spacing["04"]
  };
}
function _temp2(theme_0) {
  return {
    color: theme_0.colors.text.primary
  };
}
function _temp3(theme_1) {
  return {
    color: theme_1.colors.text.tertiary
  };
}
function _temp4(theme_3) {
  return {
    alignItems: "center",
    gap: theme_3.sizes.spacing["08"]
  };
}
function _temp5(theme_4) {
  return {
    alignItems: "center",
    gap: theme_4.sizes.spacing["05"]
  };
}
function _temp6(theme_5) {
  return {
    color: theme_5.colors.text.secondary,
    textTransform: "uppercase",
    letterSpacing: theme_5.fonts.letterSpacing.widest
  };
}
function _temp7(theme_6) {
  return {
    flexWrap: "wrap",
    justifyContent: "center",
    gap: theme_6.sizes.spacing["04"]
  };
}
function _temp8(example) {
  return <Chip key={example} size="small" variant="outlined" label={example} disabled={true} />;
}